import { z } from 'zod';
import { BusinessDateSchema, IdSchema } from './encoding.js';
/**
 * Asset Management Release 1 contracts (ERD §7.4; FRD-AST-001, AST-005 to AST-011, AST-016,
 * AST-024 to AST-027, AST-030, AST-041, AST-055, AST-064): the asset register, asset classes,
 * custody assignment and return. No tagging, transfer, disposal, maintenance or accounting value.
 *
 * The Asset ID (`code`) is generated by the system (FRD-AST-007, FRD-CORE-035): it is in every
 * response and in no request. Money crosses the wire as a decimal string (SDD §10.3).
 */
const MasterStatusSchema = z.enum(['active', 'inactive']);
const ClassCodeSchema = z
    .string()
    .regex(/^[A-Z][A-Z0-9_-]{0,19}$/, 'must be upper case, starting with a letter, up to 20 characters');
const ClassNameSchema = z.string().trim().min(1).max(100);
const NameSchema = z.string().trim().min(1).max(200);
const TextSchema = (max) => z.string().trim().min(1).max(max);
/** A list value code of `assets.asset_type` (ERD §8.7 value code shape). */
const ValueCodeSchema = z.string().regex(/^[a-z0-9][a-z0-9_]*$/, 'must be a list value code');
/**
 * The purchase cost as entered: AED with at most two decimals, never negative, never a number.
 * The API stores and returns it at exactly two decimals (`numeric(19,2)`).
 */
const CostInputSchema = z
    .string()
    .trim()
    .regex(/^\d{1,17}(\.\d{1,2})?$/, 'must be a decimal amount with at most 2 decimals');
/** The BRD §11.3 lifecycle vocabulary (FRD-AST-041), in its order. */
export const ASSET_LIFECYCLE_STATUSES = [
    'planned',
    'ordered',
    'received',
    'pending_capitalization',
    'active',
    'in_repair',
    'in_storage',
    'on_loan',
    'lost',
    'stolen',
    'impaired',
    'held_for_disposal',
    'disposed',
    'retired',
];
export const AssetLifecycleStatusSchema = z.enum(ASSET_LIFECYCLE_STATUSES);
/**
 * The states reachable by a manual status change in Release 1. Lost / stolen (FRD-AST-029,
 * approval authority CLR-11), on loan (temporary issue, FRD-AST-028), impairment and disposal
 * (Ph2 / Later) and the procurement-driven states (Ph2) are not reachable.
 */
export const R1_REACHABLE_STATUSES = ['active', 'in_storage', 'in_repair'];
/** A new asset starts in one of these (the caller chooses). */
export const INITIAL_ASSET_STATUSES = ['active', 'in_storage'];
export const CUSTODIAN_TYPES = [
    'employee',
    'department',
    'location',
    'project',
    'pool',
    'third_party',
];
export const CustodianTypeSchema = z.enum(CUSTODIAN_TYPES);
// ------------------------------------------------------------------ Asset Class
export const AssetClassSchema = z.object({
    id: IdSchema,
    code: z.string(),
    name: z.string(),
    /** Null for a class; set for a sub-class (ERD §7.4). */
    parentClassId: IdSchema.nullable(),
    status: MasterStatusSchema,
    version: z.number().int(),
});
export const CreateAssetClassRequestSchema = z.strictObject({
    code: ClassCodeSchema,
    name: ClassNameSchema,
    parentClassId: IdSchema.optional(),
});
/** The code never changes. `parentClassId: null` makes a sub-class a class again. */
export const UpdateAssetClassRequestSchema = z.strictObject({
    name: ClassNameSchema.optional(),
    parentClassId: IdSchema.nullable().optional(),
    status: MasterStatusSchema.optional(),
});
// ------------------------------------------------------------------ Asset type (pick-list)
export const AssetTypeSchema = z.object({
    valueCode: z.string(),
    label: z.string(),
    /** A retired value still labels the assets that hold it; only active ones can be chosen. */
    status: z.enum(['active', 'inactive']),
});
// ------------------------------------------------------------------ Asset
export const AssetSchema = z.object({
    id: IdSchema,
    /** The generated Asset ID. */
    code: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    assetTypeCode: z.string(),
    assetClassId: IdSchema,
    serialNumber: z.string().nullable(),
    manufacturer: z.string().nullable(),
    model: z.string().nullable(),
    purchaseDate: z.string().nullable(),
    /** AED, exactly two decimals, as a string. */
    purchaseCost: z.string().nullable(),
    /**
     * Accumulated depreciation as migrated (FRD-AST-015; ERD §7.4), AED with two decimals; set only
     * by a migration load, and null for every other asset. Not an accounting value (FRD-MIG-017).
     */
    openingAccumulatedDepreciation: z.string().nullable().default(null),
    supplierId: IdSchema.nullable(),
    locationId: IdSchema.nullable(),
    departmentId: IdSchema.nullable(),
    costCenterId: IdSchema.nullable(),
    projectId: IdSchema.nullable(),
    warrantyProvider: z.string().nullable(),
    warrantyExpiryDate: z.string().nullable(),
    /**
     * The procurement source (FRD-AST-012, AST-016): the purchase order line and goods receipt line,
     * and the order and receipt they belong to. Identifiers only; defaulted for older responses.
     */
    purchaseOrderLineId: IdSchema.nullable().default(null),
    goodsReceiptLineId: IdSchema.nullable().default(null),
    purchaseOrderId: IdSchema.nullable().default(null),
    goodsReceiptId: IdSchema.nullable().default(null),
    lifecycleStatus: AssetLifecycleStatusSchema,
    disposalDate: z.string().nullable(),
    /** The current custodian: the open custody assignment, if any (derived, ERD §7.4). */
    currentAssignmentId: IdSchema.nullable(),
    currentAssignmentVersion: z.number().int().nullable(),
    custodianType: CustodianTypeSchema.nullable(),
    custodianEmployeeId: IdSchema.nullable(),
    custodianDepartmentId: IdSchema.nullable(),
    custodianLocationId: IdSchema.nullable(),
    custodianProjectId: IdSchema.nullable(),
    custodianThirdPartyName: z.string().nullable(),
    custodySince: z.string().nullable(),
    acknowledgementRequired: z.boolean().nullable(),
    acknowledgedAt: z.string().nullable(),
    /** For `If-Match` on a change, a status change or an assignment (SDD §7.3). */
    version: z.number().int(),
});
export const CreateAssetRequestSchema = z.strictObject({
    name: NameSchema,
    description: TextSchema(2000).optional(),
    assetTypeCode: ValueCodeSchema,
    assetClassId: IdSchema,
    serialNumber: TextSchema(100).optional(),
    manufacturer: TextSchema(200).optional(),
    model: TextSchema(200).optional(),
    purchaseDate: BusinessDateSchema.optional(),
    purchaseCost: CostInputSchema.optional(),
    supplierId: IdSchema.optional(),
    locationId: IdSchema.optional(),
    departmentId: IdSchema.optional(),
    costCenterId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    warrantyProvider: TextSchema(200).optional(),
    warrantyExpiryDate: BusinessDateSchema.optional(),
    /** The procurement source (FRD-AST-012, AST-016): a purchase order line and a GRN line. */
    purchaseOrderLineId: IdSchema.optional(),
    goodsReceiptLineId: IdSchema.optional(),
    lifecycleStatus: z.enum(INITIAL_ASSET_STATUSES),
});
/**
 * A partial change; an optional field is cleared with `null`. The Asset ID, the lifecycle status
 * (a status change is its own action) and the disposal date (Ph2) are not changed here.
 */
export const UpdateAssetRequestSchema = z.strictObject({
    name: NameSchema.optional(),
    description: TextSchema(2000).nullable().optional(),
    assetTypeCode: ValueCodeSchema.optional(),
    assetClassId: IdSchema.optional(),
    serialNumber: TextSchema(100).nullable().optional(),
    manufacturer: TextSchema(200).nullable().optional(),
    model: TextSchema(200).nullable().optional(),
    purchaseDate: BusinessDateSchema.nullable().optional(),
    purchaseCost: CostInputSchema.nullable().optional(),
    supplierId: IdSchema.nullable().optional(),
    locationId: IdSchema.nullable().optional(),
    departmentId: IdSchema.nullable().optional(),
    costCenterId: IdSchema.nullable().optional(),
    projectId: IdSchema.nullable().optional(),
    warrantyProvider: TextSchema(200).nullable().optional(),
    warrantyExpiryDate: BusinessDateSchema.nullable().optional(),
    purchaseOrderLineId: IdSchema.nullable().optional(),
    goodsReceiptLineId: IdSchema.nullable().optional(),
});
/** A manual lifecycle status change with its mandatory reason (FRD-AST-041, AST-050). */
export const ChangeAssetStatusRequestSchema = z.strictObject({
    toStatus: AssetLifecycleStatusSchema,
    effectiveDate: BusinessDateSchema,
    reason: TextSchema(500),
});
export const AssetLifecycleEventSchema = z.object({
    id: IdSchema,
    assetId: IdSchema,
    eventType: z.literal('status_change'),
    fromStatus: z.string(),
    toStatus: z.string(),
    effectiveDate: z.string(),
    reason: z.string(),
    actorUserId: IdSchema.nullable(),
    recordedAt: z.string(),
});
// ------------------------------------------------------------------ Custody
/**
 * Assign an asset (FRD-AST-024). Exactly the reference the custodian type needs: an employee,
 * department, location or project id, a third party's name, or nothing for the pool.
 */
export const AssignAssetRequestSchema = z.strictObject({
    custodianType: CustodianTypeSchema,
    employeeId: IdSchema.optional(),
    departmentId: IdSchema.optional(),
    locationId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    thirdPartyName: TextSchema(200).optional(),
    assignmentDate: BusinessDateSchema,
});
/** Record a return (FRD-AST-027): the date and the condition at return, as free text. */
export const ReturnAssetRequestSchema = z.strictObject({
    returnDate: BusinessDateSchema,
    condition: TextSchema(500),
});
export const CustodyAssignmentSchema = z.object({
    id: IdSchema,
    assetId: IdSchema,
    custodianType: CustodianTypeSchema,
    employeeId: IdSchema.nullable(),
    departmentId: IdSchema.nullable(),
    locationId: IdSchema.nullable(),
    projectId: IdSchema.nullable(),
    thirdPartyName: z.string().nullable(),
    assignmentDate: z.string(),
    assignedBy: IdSchema,
    acknowledgementRequired: z.boolean(),
    acknowledgedAt: z.string().nullable(),
    acknowledgedBy: IdSchema.nullable(),
    returnDate: z.string().nullable(),
    returnedBy: IdSchema.nullable(),
    returnCondition: z.string().nullable(),
    status: z.enum(['open', 'closed']),
    closedDate: z.string().nullable(),
    /** How it closed: a recorded return, or the next assignment of the asset. */
    closure: z.enum(['returned', 'reassigned']).nullable(),
    version: z.number().int(),
});
//# sourceMappingURL=assets.js.map